import { create } from "zustand";

// Random initial world assignment (only computed once per session)
const _initialWorld = (() => {
  if (typeof window === "undefined") return "earth";
  try {
    // TEMP: force underwater for screenshot verification
    const q = new URLSearchParams(window.location.search);
    const forced = q.get("world");
    if (forced === "earth" || forced === "space" || forced === "underwater") return forced;
    const r = Math.random();
    if (r < 0.34) return "earth";
    if (r < 0.67) return "space";
    return "underwater";
  } catch {
    return "earth";
  }
})();

export const useWorldStore = create((set, get) => ({
  // ID of the airport/planet the plane is currently near (or null)
  nearestId: null,
  // Distance to nearest airport (for HUD)
  nearestDistance: Infinity,
  // ID of the airport whose panel is currently open (or null)
  openId: null,
  // Live plane telemetry for HUD
  altitude: _initialWorld === "underwater" ? -18 : (_initialWorld === "space" ? 120 : 20),
  speed: 0,
  heading: 0, // yaw in radians
  position: {
    x: 0,
    y: _initialWorld === "underwater" ? -18 : (_initialWorld === "space" ? 120 : 20),
    z: 30,
  },
  // Camera direction (unit vector) - updated by Airplane, consumed by Sun for dazzle
  cameraDir: { x: 0, y: 0, z: -1 },
  // World readiness
  ready: false,
  // Input state that mobile touch controls can drive; keyboard also mirrors here
  input: { up: false, down: false, left: false, right: false, boost: false, escape: false },
  // Bird gameplay
  birdHits: 0,
  lastHitAt: 0,
  // Dazzle intensity 0..1 (updated by Sun)
  dazzle: 0,
  // Space mode
  spaceMode: _initialWorld === "space",
  // Underwater mode
  underwaterMode: _initialWorld === "underwater",
  // Escape charge 0..1 - accumulates while shift held and altitude>threshold
  escapeCharge: 0,
  // Transition phase: "idle" | "ascending" | "space" | "descending"
  transitionPhase: "idle",
  transitionProgress: 0, // 0..1 ease value
  // Parcels dropped
  parcels: [],
  parcelCounter: 0,
  // Player lasers (space mode)
  playerLasers: [],
  playerLaserCounter: 0,
  // Meteor strikes (asteroid collisions in space) - reuses birdHits internally

  setNearest: (id, distance) => set({ nearestId: id, nearestDistance: distance }),
  openPanel: (id) => set({ openId: id }),
  closePanel: () => set({ openId: null }),
  setTelemetry: (t) => set(t),
  setReady: (v) => set({ ready: v }),
  setInput: (patch) => set((state) => ({ input: { ...state.input, ...patch } })),
  resetInput: () => set({ input: { up: false, down: false, left: false, right: false, boost: false, escape: false } }),
  registerBirdHit: () => set((s) => ({ birdHits: s.birdHits + 1, lastHitAt: performance.now() })),
  resetBirdHits: () => set({ birdHits: 0, lastHitAt: 0 }),
  setDazzle: (v) => set({ dazzle: v }),
  setCameraDir: (v) => set({ cameraDir: v }),

  // Space mode actions
  setSpaceMode: (v) => set({ spaceMode: v }),
  setUnderwaterMode: (v) => set({ underwaterMode: v }),
  setEscapeCharge: (v) => set({ escapeCharge: v }),
  setTransition: (phase, progress) => set({ transitionPhase: phase, transitionProgress: progress }),

  // Parcels
  dropParcel: (position, heading) => {
    const id = get().parcelCounter + 1;
    const now = performance.now();
    set((s) => ({
      parcelCounter: id,
      parcels: [
        ...s.parcels,
        {
          id,
          x: position.x,
          y: position.y - 1.2,
          z: position.z,
          vx: -Math.sin(heading) * 4,
          vy: 0,
          vz: -Math.cos(heading) * 4,
          bornAt: now,
          chuteOpen: false,
        },
      ],
    }));
  },
  removeParcel: (id) => set((s) => ({ parcels: s.parcels.filter((p) => p.id !== id) })),
  updateParcel: (id, patch) => set((s) => ({
    parcels: s.parcels.map((p) => (p.id === id ? { ...p, ...patch } : p)),
  })),

  // Player lasers - fired from our ship in space mode
  firePlayerLaser: (position, heading, pitch) => {
    const now = performance.now();
    const speed = 90;
    // Forward direction
    const fx = -Math.sin(heading) * Math.cos(pitch);
    const fy = -Math.sin(pitch);
    const fz = -Math.cos(heading) * Math.cos(pitch);
    // Compute right vector for slight spread
    const rx = Math.cos(heading);
    const rz = -Math.sin(heading);
    const shots = [];
    for (let i = 0; i < 3; i++) {
      const spread = (i - 1) * 0.7; // -0.7, 0, 0.7 offset
      shots.push({
        id: get().playerLaserCounter + 1 + i,
        x: position.x + rx * spread,
        y: position.y - 0.3,
        z: position.z + rz * spread,
        vx: fx * speed,
        vy: fy * speed,
        vz: fz * speed,
        bornAt: now,
      });
    }
    set((s) => ({
      playerLaserCounter: s.playerLaserCounter + 3,
      playerLasers: [...s.playerLasers, ...shots],
    }));
  },
  removePlayerLaser: (id) => set((s) => ({ playerLasers: s.playerLasers.filter((p) => p.id !== id) })),

  // Bubbles (underwater mode)
  bubbles: [],
  bubbleCounter: 0,
  releaseBubbles: (position) => {
    const now = performance.now();
    const start = get().bubbleCounter + 1;
    const shots = [];
    for (let i = 0; i < 14; i++) {
      shots.push({
        id: start + i,
        x: position.x + (Math.random() - 0.5) * 1.6,
        y: position.y - 0.7,
        z: position.z + (Math.random() - 0.5) * 1.6,
        wobble: Math.random() * Math.PI * 2,
        size: 0.15 + Math.random() * 0.3,
        rise: 2.5 + Math.random() * 2,
        bornAt: now,
      });
    }
    set((s) => ({ bubbleCounter: s.bubbleCounter + 14, bubbles: [...s.bubbles, ...shots] }));
  },
  removeBubble: (id) => set((s) => ({ bubbles: s.bubbles.filter((b) => b.id !== id) })),
}));
